import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DynamicPopup from './DynamicPopup';
import { POPUP_CONFIGS } from '../popupConfigs';
import builtInApiMap from '../definitions';
import { getCustomForms } from '../../../services/adminApi';
import { fetchJsonWithAuth } from '../../../services/authApi';
import { AUTH_URL } from '../../../services/apiConfig';
import AddBatchCandidatePopup from '../AddBatchCandidatePopup';

const POPUP_FIELD_TYPES = {
  text: 'input',
  string: 'input',
  textarea: 'textarea',
  date: 'date-picker',
  datepicker: 'date-picker',
  multiselect: 'multi-select',
  lookup: 'search-select',
};

function toPopupConfig(form) {
  if (!form) return null;
  const groups = [...(form.groups ?? [])].sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
  const fields = groups.flatMap((group) => (
    [...(group.fields ?? [])]
      .filter((field) => field.show !== 0 && field.show !== false)
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
      .map((field) => ({
        ...field,
        name: field.name ?? field.field,
        label: field.label ?? field.field,
        type: POPUP_FIELD_TYPES[String(field.type ?? 'text').toLowerCase()] ?? field.type ?? 'input',
        required: field.required ?? field.req === 1,
        options: (field.options ?? []).map((option) => ({
          label: option.label ?? option.name ?? option.value,
          value: option.value ?? option.id ?? option.label,
        })),
      }))
  ));

  return { title: form.label, fields };
}

/**
 * Route wrapper for DynamicPopup.
 *
 * Drop straight into your App.jsx popup routes — NO props required.
 * The built-in apiMap and POPUP_CONFIGS are used automatically:
 *
 *   <Route path="/popup/:popupKey" element={<DynamicPopupRoute />} />
 *
 * To add project-specific popups alongside the built-ins:
 *
 *   const myConfigs = { 'send-offer': { title: 'Send Offer', fields: [...] } };
 *   const myApiMap  = { 'send-offer': { submit: async (values, ctx) => { ... } } };
 *   <Route path="/popup/:popupKey"
 *     element={<DynamicPopupRoute apiMap={myApiMap} extraConfigs={myConfigs} />} />
 *
 * Props:
 *   apiMap       — merged on top of the built-in handlers, so you only need to
 *                  list NEW keys; built-in popup APIs (delete, add-notes, …) keep working.
 *   extraConfigs — same merge pattern for configs.
 */
export default function DynamicPopupRoute({ apiMap = {}, extraConfigs = {} }) {
  // Merge: caller's entries win; built-ins fill the gaps.
  const mergedApiMap = useMemo(() => ({ ...builtInApiMap, ...apiMap }), [apiMap]);
  const mergedConfigs = useMemo(() => {
    const configs = { ...POPUP_CONFIGS };
    Object.entries(extraConfigs).forEach(([key, override]) => {
      configs[key] = { ...(configs[key] ?? {}), ...override };
    });
    return configs;
  }, [extraConfigs]);
  const { popupKey } = useParams();
  const navigate = useNavigate();
  const { state = {} } = useLocation();

  const [adminConfigResult, setAdminConfigResult] = useState({ key: '', config: null });

  useEffect(() => {
    let cancelled = false;
    if (!popupKey || !state.moduleName) {
      return undefined;
    }
    const lookupKey = `${state.moduleName}:${popupKey}`;
    getCustomForms(state.moduleName, 'popup', popupKey)
      .then((forms) => {
        if (!cancelled) setAdminConfigResult({ key: lookupKey, config: toPopupConfig(forms[0]) });
      })
      .catch(() => {
        if (!cancelled) setAdminConfigResult({ key: lookupKey, config: null });
      });
    return () => { cancelled = true; };
  }, [popupKey, state.moduleName]);

  const config = useMemo(() => {
    const builtIn = mergedConfigs[popupKey];
    const lookupKey = `${state.moduleName}:${popupKey}`;
    const adminConfig = adminConfigResult.key === lookupKey ? adminConfigResult.config : null;
    if (!adminConfig) return builtIn;
    return { ...builtIn, ...adminConfig };
  }, [adminConfigResult, mergedConfigs, popupKey, state.moduleName]);

  // Memoized so that router re-renders don't create a new object reference on every tick,
  // which would otherwise cause DynamicPopup's effects to re-run unnecessarily.
  const context = useMemo(
    () => ({
      selectedIds: state.selectedIds ?? [],
      selectedRecords: state.selectedRecords ?? [],
      moduleName: state.moduleName ?? '',
      navigate,
      // The page this popup opened over (row action stamps state.background).
      // Exposed so a custom-body popup can hand off to a SIBLING popup over the
      // same page — e.g. Manager Approve's Reject opening the Reject popup —
      // without the page vanishing behind it.
      background: state.background ?? null,
      ...(state.popupContext ?? {}),
    }),
    [navigate, state.selectedIds, state.selectedRecords, state.moduleName, state.popupContext, state.background],
  );

  const configuredApi = useMemo(() => {
    const popupAction = state.popupAction;
    if (!popupAction?.apiPath) return {};
    return {
      submit: (values, popupContext) => {
        const method = String(popupAction.apiMethod || 'POST').toUpperCase();
        return fetchJsonWithAuth(
          AUTH_URL,
          popupAction.apiPath.startsWith('/') ? popupAction.apiPath : `/${popupAction.apiPath}`,
          {
            method,
            body: ['GET', 'DELETE'].includes(method) ? undefined : JSON.stringify({
              ...values,
              selectedIds: popupContext.selectedIds,
              selectedRecords: popupContext.selectedRecords,
              moduleName: popupContext.moduleName,
            }),
          },
        );
      },
    };
  }, [state.popupAction]);

  // Closing a route-mounted popup returns to the page it was opened from.
  // On a SUCCESSFUL mutation (delete, assign, change-status, tag-to-job, …)
  // the caller passes { success: true }; we then return to the background list
  // location stamped with a fresh `refreshTimestamp`, which ListView watches to
  // refetch its listing (and clear the row selection) — so every module's popup
  // actions refresh the list automatically instead of leaving stale rows on
  // screen. A plain cancel/X (no result) just pops history, preserving the
  // list's scroll/state untouched.
  const handlePopupClose = (result) => {
    const background = state.background;
    // A popup may declare config.successNavigate to send the user to a specific
    // route on success (e.g. Move to Onboarding → /onboarding) instead of
    // returning to the list it was opened from. Still stamps refreshTimestamp
    // so the destination refetches.
    if (result?.success && config?.successNavigate) {
      navigate(config.successNavigate, {
        replace: true,
        state: { refreshTimestamp: Date.now() },
      });
      return;
    }
    if (result?.success && background?.pathname) {
      navigate(`${background.pathname}${background.search ?? ''}`, {
        replace: true,
        state: { ...(background.state ?? {}), refreshTimestamp: Date.now() },
      });
      return;
    }
    navigate(-1);
  };

  if (popupKey === 'add-batch-candidate') {
    return (
      <AddBatchCandidatePopup
        open
        context={context}
        onClose={handlePopupClose}
      />
    );
  }

  return (
    <DynamicPopup
      open
      config={config}
      context={context}
      onClose={handlePopupClose}
      api={{ ...(mergedApiMap[popupKey] ?? {}), ...configuredApi }}
    />
  );
}
